import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { makeAutoObservable } from "mobx"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import WebSocketStore from "websockets/WebSocketStore"
import WebSocketEventClientListUpdate from "../websockets/models/WebSocketEventClientListUpdate"
import AdvisorBlueprintCacheStore from "./AdvisorBlueprintCacheStore"
import WebSocketEventAdvisorBlueprintListUpdate from "../websockets/models/WebSocketEventAdvisorBlueprintListUpdate"
import WebSocketProcessChangeEvent from "../websockets/models/WebSocketProcessChangeEvent"
import ClientsCacheStore from "./ClientsCacheStore"
import ProcessCacheStore from "./ProcessCacheStore"
import userCache from "./userCache"
import { IDBMetricsCache } from "./idb/IDBMetricsCache"

interface CacheStoreProps {
   webSocketStore: WebSocketStore
   orgGuardStore: OrgGuardStore
   advisorBlueprintCacheStore: AdvisorBlueprintCacheStore
   processCacheStore: ProcessCacheStore
   clientsCacheStore: ClientsCacheStore
}

export default class CacheStore {
   sp
   hasLoaded = false

   constructor(sp: CacheStoreProps) {
      makeAutoObservable(this)
      this.sp = sp

      sp.webSocketStore.addSubscriptions({
         orgId: sp.orgGuardStore.userData.userOrg.orgId,
         userId: sp.orgGuardStore.userData.id,
         subscriptions: [
            {
               entityId: sp.orgGuardStore.userData.userOrg.orgId,
               event: 'client-list-update',
               callback: async event => {
                  return this.sp.clientsCacheStore.handleClientsEvent(JSON.parse(event.message) as WebSocketEventClientListUpdate)
               }
            },
            {
               entityId: sp.orgGuardStore.userData.userOrg.orgId,
               event: 'document-template-event',
               callback: async event => {
                  return this.sp.advisorBlueprintCacheStore.handleBlueprintEvent(JSON.parse(event.message) as WebSocketEventAdvisorBlueprintListUpdate)
               }
            },
            {
               entityId: sp.orgGuardStore.userData.userOrg.orgId,
               event: 'process-change-event',
               callback: async event => {
                  return this.processCacheStore.handleProcessChangeEvent(JSON.parse(event.message) as WebSocketProcessChangeEvent)
               }
            },
            {
               entityId: sp.orgGuardStore.userData.userOrg.orgId,
               event: 'metrics-change-event',
               callback: async event => {
                  return IDBMetricsCache.invalidate()
               }
            }
         ]
      })
   }

   get processCacheStore() {
      return this.sp.processCacheStore
   }

   get clientsCacheStore() {
      return this.sp.clientsCacheStore
   }

   get advisorBlueprintCacheStore() {
      return this.sp.advisorBlueprintCacheStore
   }

   fetch = async () => {
      if (this.sp.orgGuardStore.userOrg.orgType === 'advisor') {
         const isServiceUser = userCache.serviceUser.get()
         if (!isServiceUser) {
            await Promise.all([
               this.clientsCacheStore.fetch(),
               this.advisorBlueprintCacheStore.fetch(),
               this.processCacheStore.fetch()
            ])
         }
      }

      this.hasLoaded = true
   }
}

export const { Provider: CacheStoreProvider, useProvidedStore: useCacheStore } = createStoreContext(CacheStore)
